import {
  User,
  Plans,
  Order,
  FPError,
  Currency,
  Lang,
  Analytics,
  Payment,
} from 'types';
import { GetterTree, MutationTree, ActionTree, ActionContext } from 'vuex';
import { ExchangeService, NetworkHelper, UserService } from 'api';
import { datadogRum } from '@datadog/browser-rum';
import { finalize } from 'rxjs/operators';
import { Events } from '@/analytics';
import store from '..';
import router from 'router';
import { Log, mapError } from '@/utils';
import { intercom } from '@/plugins';
import { guardedPaths } from '@/router';
import { parsePhoneNumber } from 'awesome-phonenumber';

export class State {
  user: User.User = null;
  userV2: User.UserV2 = null;
  loading: boolean = false;
  contacts: User.Contacts = new User.Contacts();
  guest: User.Guest = new User.Guest();
  split: User.Split = new User.Split();
  newTraveller: User.NewTraveller = new User.NewTraveller();
  screens: Plans.Screens = null;
  failureDialog: Order.FailureDialog = new Order.FailureDialog();
  authDialogShown: boolean = false;
  registerDialogShown: boolean = false;
  credits: User.UserCreditsState = new User.UserCreditsState();
  payoutFailureDialog: Order.FailureDialog = new Order.FailureDialog();
  isUserCategorySwitcherShown: boolean = User.UserCategory.isSwitcherShown();
  category: User.UserCategory.Category = User.UserCategory.get();

  // new profile
  isPayoutModalShown: boolean = false;

  // pending hotel to open after auth
  pendingHotelToOpen: {
    hotelId: number;
    query: Record<string, string>;
    filters?: string;
    isMobile: boolean;
  } | null = null;
}

const getters = <GetterTree<State, any>>{
  subscription(state: State): User.Subscription {
    if (!state.user) return null;
    return User.getSubscription(state.user);
  },

  cards(state: State): User.Card[] {
    const savedCards = Payment.Card.getSavedCardsAsUserCards(
      state.user?.user_id,
    );

    return savedCards;
  },

  travellers(state: State): string[] {
    return state.userV2?.travelers ?? [];
  },
  getPhoneFormatted(state: State) {
    const phone = state.contacts.phone.startsWith('+')
      ? state.contacts.phone
      : `+${state.contacts.phone}`;
    const ph = parsePhoneNumber(phone);
    return ph.number?.international
      ? ph.number?.international
      : state.contacts.phone;
  },
  getDefaultCard(state: State) {
    if (state.user === null) return null;

    const default_card_id = state.user.default_card_id;
    if (default_card_id) {
      const default_card = state.user.payment_cards.find(
        it => it.card_id === default_card_id,
      );
      return default_card ? default_card : null;
    } else return null;
  },
  getIsBin4000(state: State): boolean {
    return (
      state.userV2 &&
      state.userV2.bin &&
      state.userV2.bin.replace(/ /g, '').trim().startsWith('40004000')
    );
  },
  userPhoneFormatted(state: State) {
    const phone = state.userV2?.phone?.startsWith('+')
      ? state.userV2?.phone
      : `+${state.userV2?.phone}`;
    const ph = parsePhoneNumber(phone);
    return ph.number?.international
      ? ph.number?.international
      : state.userV2?.phone ?? '';
  },

  ordersFilteredByStatus(state: State) {
    const allowedStatuses = [
      Order.Ml.OrderStatus.Booked,
      Order.Ml.OrderStatus.Canceled,
      Order.Ml.OrderStatus.Pending,
      Order.Ml.OrderStatus.Need2fa,
      Order.Ml.OrderStatus.Completed,
      Order.Ml.OrderStatus.Stayed,
    ];
    return state.userV2?.orders?.filter(order =>
      allowedStatuses.includes(order.status),
    );
  },
};

const mutations = <MutationTree<State>>{
  setEmail(state: State, email: string) {
    state.contacts.email = email;
  },
  setLoading(state: State, loading: boolean) {
    state.loading = loading;
  },
  setFirstName(state: State, name: string) {
    state.newTraveller.firstName = name;
  },
  setLastName(state: State, name: string) {
    state.newTraveller.lastName = name;
  },
  setTrevellerCreationError(state: State, message: string) {
    state.newTraveller.error = message;
  },
  setTraveller(state: State, traveller: string) {
    state.guest.traveller = traveller;
    // if (traveller) Events.selectTraveler();
  },
  setNewTravellerSubmitted(state: State, value: boolean) {
    state.newTraveller.isSubmitted = value;
  },

  setGuest(state: State, valid: boolean) {
    state.guest.created = valid;
    state.guest.isSubmitted = !valid;
  },

  setContacts(state: State, valid: boolean) {
    state.contacts.created = valid;
    state.contacts.isSubmitted = !valid;
  },
  setUser(state: State, user: User.User) {
    state.user = user ?? null;
  },

  invalidateGuest(state: State) {
    state.guest = new User.Guest();
  },

  invalidateContacts(state: State) {
    state.contacts = new User.Contacts();
  },

  setPhone(state: State, phone: string) {
    const _phone = phone.startsWith('+') ? phone : `+${phone}`;
    const ph = parsePhoneNumber(_phone);
    state.contacts.phone = phone;
    state.contacts.phoneIsValid = phone ? ph?.valid : true;
  },

  setFlightNumber(state: State, flightNumber: string) {
    state.contacts.flightNumber = flightNumber;
  },

  pushTraveller(state: State, traveller: string) {
    state.userV2.travelers.push(traveller);
    state.guest.traveller = traveller;
  },

  autoSelectCurrentPlan(state: State) {
    if (state.split.screens && state.split.currentPlan) {
      const currentPlan = state.split.screens.SUBS_OPTIONS.plans.find(
        it => it.id === state.split.currentPlan.planId,
      );
      state.split.plan = 'purchase';
      state.split.planModel = currentPlan;
    }
  },

  showTravellerModel(state: State, show: boolean) {
    state.newTraveller = new User.NewTraveller();
    state.newTraveller.isModalOpen = show;
    // if (show) Events.addNewTraveler();
  },

  setCards(state: State, cards: User.Card[]) {
    state.user.payment_cards = cards;
  },

  addCard(state: State, card: User.Card) {
    if (card && state.user && state.user.payment_cards) {
      state.user.payment_cards.push(card);
    }
  },

  removeTmpCardById(state: State, id: string) {
    if (state.user && state.user.payment_cards) {
      const indexOfCard = state.user.payment_cards.findIndex(
        it => it.temporary && it.card_id === id,
      );
      if (indexOfCard >= 0) state.user.payment_cards.splice(indexOfCard, 1);
    }
  },

  setAuthFailure(state: State, error: FPError) {
    state.failureDialog.message = error.message;
    state.failureDialog.error = 'Oops!';
    state.failureDialog.show = error ? true : false;
  },
  showAuthFailure(state: State, show: boolean) {
    state.failureDialog.show = show;
  },

  setAuthDialogShown(state: State, show: boolean) {
    state.authDialogShown = show;
    if (show) {
      Events.Auth.signInStart();
    }
  },

  setRegisterDialogShown(state: State, show: boolean) {
    state.registerDialogShown = show;
    if (show) {
      Events.Auth.signUpStart();
    }
  },

  setUserCredits(state: State, credits: User.UserCredits) {
    state.credits.credits = credits;
  },

  setUserCreditsLoading(state: State, isLoading: boolean) {
    state.credits.isLoading = isLoading;
  },

  setUserCreditsError(state: State, error: string) {
    state.credits.error = error;
  },

  showPayoutFailureDialog(state: State, show: boolean) {
    state.payoutFailureDialog.show = show;
  },

  toggleUserCategorySwitcher(state: State, isShown: boolean) {
    state.isUserCategorySwitcherShown = isShown;
    User.UserCategory.savePricingMode(User.UserCategory.Category.Individual);
  },

  setUserCategory(state: State, category: User.UserCategory.Category) {
    state.category = category;
    User.UserCategory.set(category);
  },

  setUserV2(state: State, _user: User.UserV2) {
    state.userV2 = _user;
  },

  setPayoutModalShown(state: State, isShown: boolean) {
    state.isPayoutModalShown = isShown;
  },

  setPendingHotelToOpen(
    state: State,
    data: {
      hotelId: number;
      query: Record<string, string>;
      filters?: string;
      isMobile: boolean;
    } | null,
  ) {
    state.pendingHotelToOpen = data;
  },
};

const actions = <ActionTree<State, any>>{
  getUser(store: ActionContext<State, any>) {
    const credentials = NetworkHelper.getAuthCredentials();

    const hasCredentials = credentials?.sessionId && credentials?.userId;

    if (!hasCredentials) {
      return new Promise(resolve => {
        store.commit('setLoading', false);
        resolve(false);
      });
    }
    store.commit('setLoading', true);

    return new Promise(resolve => {
      store.commit('setLoading', true);

      UserService.ML.getUserV2()
        .pipe(finalize(() => store.commit('setLoading', false)))
        .subscribe(
          res => {
            store.dispatch('populateUserV2', res);
            resolve(true);
          },
          _ => {
            if (localStorage.getItem('view')) {
              window.open('/signin', '_self');
            }
            resolve(false);
          },
        );
    });
  },

  updateUserCards(store: ActionContext<State, any>, cards: User.Card[]) {
    store.commit('setCards', cards);
  },

  addTempCard(store: ActionContext<State, any>, card: User.Card) {
    const indexOfCard = store.getters.cards.findIndex(
      it => it.card_id === card.card_id,
    );

    if (indexOfCard >= 0)
      store.dispatch('card/selectCard', indexOfCard, { root: true });
  },

  logout(store: ActionContext<State, any>) {
    return new Promise(resolve => {
      store.commit('setLoading', true);

      store.dispatch('invalidateUser');
    });
  },

  async invalidateUser(
    store: ActionContext<State, any>,
    sendEvent: boolean = true,
  ) {
    store.commit('invalidateGuest');
    store.commit('invalidateContacts');
    store.commit('setUser', null);
    store.commit('setUserV2', null);

    if (router.currentRoute.value.name === 'hotel') {
      store.dispatch('hotel/selectOffer', { offer: null }, { root: true });
      store.dispatch('booking/selectCard', null, { root: true });
    }

    if (guardedPaths.includes(String(router.currentRoute.value.name))) {
      router.push('/');
    }

    NetworkHelper.clearCredentials();

    intercom.update({});
    intercom.shutdown();

    if (sendEvent) {
      Events.Auth.logout();
    }
  },

  addTraveller(store: ActionContext<State, any>) {
    store.commit('pushTraveller', store.state.newTraveller.fullName);
    store.commit('showTravellerModel', false);

    Events.Hotel.Offer.guestAdded();
  },

  populateUserV2(store: ActionContext<State, any>, user: User.UserV2) {
    store.commit('setUserV2', user);
    store.commit('setEmail', user.email ?? '');
    store.commit('setUserCategory', user.type);
    User.UserCategory.clearPricingMode();
    updateTraitsOnAuth(user);

    // intercom
    intercom.shutdown();
    intercom.boot(user);
    store.dispatch('support/updateIntercomSettings', {}, { root: true });

    store.dispatch('support/getInfoUtils', {}, { root: true });

    // set saved order details
    const persistence = User.PersistenceGuestData.get();

    if (user.travelers?.length > 0) {
      let traveler = user.travelers[0];
      if (
        persistence &&
        persistence.traveler &&
        user.travelers.some(it => it === persistence.traveler)
      ) {
        traveler = user.travelers.find(it => it === persistence.traveler);
      }

      if (traveler) {
        store.commit('setTraveller', traveler);
      }
    }

    if (persistence && persistence.specialRequest) {
      store.commit('booking/setSpecialRequest', persistence.specialRequest, {
        root: true,
      });
    }

    if (persistence && persistence.arrivalTime) {
      store.commit('booking/setArrivalTime', persistence.arrivalTime, {
        root: true,
      });
    }

    // datadog
    datadogRum.setUser({
      'user-id': user.userId,
    });

    // open pending hotel if exists
    store.dispatch('openPendingHotel');
  },

  onFillGuestDetails(store: ActionContext<State, any>) {
    store.commit('setContacts', true);
    store.commit('setGuest', true);

    if (
      !store.state.split.currentPlanIsOld() &&
      store.state.split.isCurrentPlanPaid()
    ) {
      store.commit('autoSelectCurrentPlan');
    }

    User.PersistenceGuestData.save({
      phone: store.state.contacts.phone,
      traveler: store.state.guest.traveller,
    });

    if (store.state.user?.cell_number !== store.state.contacts.phone) {
      Events.Hotel.Offer.phoneAdded();
    }
  },

  openPendingHotel(store: ActionContext<State, any>) {
    const pendingHotel = store.state.pendingHotelToOpen;

    if (!pendingHotel) {
      return;
    }

    const { hotelId, query, filters, isMobile } = pendingHotel;

    const queryWithFilters = { ...query };
    if (filters && filters.length > 0) {
      queryWithFilters['filters'] = filters;
    }

    if (isMobile) {
      router.push({
        path: `/hotel/${hotelId}`,
        query: queryWithFilters,
      });
    } else {
      const base = window.location.origin;
      const params = new URLSearchParams(queryWithFilters as any).toString();
      const url = `${base}/hotel/${hotelId}?${params}`;
      window.open(url);
    }

    // Очищаем сохраненные данные
    store.commit('setPendingHotelToOpen', null);
  },
};

let authTimeout;
window.onstorage = (e: StorageEvent) => {
  if (!e || !e.key) {
    return;
  }

  if (
    e.key.includes(NetworkHelper.LEGACY_USERID) ||
    e.key.includes(NetworkHelper.AUTH)
  ) {
    clearTimeout(authTimeout);
    if (!e.newValue) {
      authTimeout = setTimeout(() => {
        store.dispatch('user/invalidateUser', {}, { root: true });
      }, 1000);

      Log.onLocalStorageCleared();
    } else {
      authTimeout = setTimeout(() => {
        store.dispatch('user/getUser', {}, { root: true });
      }, 1000);
    }
  } else if (e.key.includes(Currency.CURRENCY_COOKIE_NAME)) {
    if (e.newValue) {
      const currency = Currency.currenciesList().find(
        it => it.currency === e.newValue,
      );

      if (currency) {
        store.commit('support/setCurrency', currency, { root: true });
      }
    }
  } else if (e.key.includes(Lang.LANG_COOKIE_NAME)) {
    if (e.newValue) {
      const locale = Lang.langsList().find(it => it.locale === e.newValue);

      if (locale) {
        store.commit('support/setLang', locale, { root: true });
      }
    }
  }
};

/**
 * @deprecated
 * now using populateUserV2 action
 */
function populateUser(
  store: ActionContext<State, any>,
  res: User.UserRepsonse,
) {
  if (res.user) {
    store.commit('setUser', res.user);

    // moved to populateUserV2
    //
    // store.dispatch('getCountries');

    const subscription = User.getSubscription(res.user);

    if (
      subscription &&
      subscription.travellers &&
      subscription.travellers.length === 0 &&
      res.user.first_name &&
      res.user.last_name
    ) {
      // SME-991
      addNewTraveller(store, res.user);
    }

    // moved to populateUserV2
    // const persistence = User.PersistenceGuestData.get();

    // if (
    //   persistence &&
    //   persistence.travellerId &&
    //   subscription.travellers &&
    //   subscription.travellers.length > 0 &&
    //   subscription.travellers.some(
    //     it => it.traveller_id === persistence.travellerId,
    //   )
    // ) {
    //   const traveller = subscription.travellers.find(
    //     it => it.traveller_id === persistence.travellerId,
    //   );

    //   if (traveller) {
    //     store.commit('setTraveller', traveller);
    //   }
    // }

    //
    // if (persistence && persistence.specialRequest) {
    //   store.commit('booking/setSpecialRequest', persistence.specialRequest, {
    //     root: true,
    //   });
    // }
    //
    // if (persistence && persistence.arrivalTime) {
    //   store.commit('booking/setArrivalTime', persistence.arrivalTime, {
    //     root: true,
    //   });
    // }

    // moved to populateUserV2
    //
    // datadogRum.setUser({
    //   'user-id': res.user.user_id,
    // });

    // FW-384
    // Hotjar.identify(res.user.user_id, {
    //   is_tester: res.user.user_groups.includes('tester'),
    // });

    if (res.pending_3ds_orders && res.pending_3ds_orders.length > 0) {
      const firstOrder = res.pending_3ds_orders[0];
      if (firstOrder && firstOrder.thrred_url) {
        store.dispatch(
          'booking/onDeferredOrderPayment',
          {
            orderId: firstOrder.id,
            thrredUrl: firstOrder.thrred_url,
            source: Order.PendingOrderSource.User,
          },
          {
            root: true,
          },
        );
      }
    }
  }
}

function addNewTraveller(store: ActionContext<State, any>, user: User.User) {
  store.commit('setFirstName', user.first_name);
  store.commit('setLastName', user.last_name);
  store.commit('setDob', '1990-01-01');

  store.dispatch('addTraveller');
}

const updateTraitsOnAuth = (user: User.UserV2) => {
  try {
    const trackModel: Analytics.Traits = {
      ...Events.getTraits(),
      session_id: NetworkHelper.getAuthCredentials()?.sessionId,
      user_id: user.userId,
      email: user.email,
      name: user.firstName,
      // is_tester: user.user_groups.includes('tester'),
      bin: user.bin,
      // signup_date: user.created_at,
      // total_savings: user.subscriptions[0]?.savings
      //   ? Math.floor(user.subscriptions[0].savings.formatCent())
      //   : 0,
      th_books: user.orders?.length ?? 0,
      credits_active: user?.credits?.active?.roundCent(),
      credits_pending: user?.credits?.pending?.roundCent(),
    };

    // if (user.register_params) {
    //   trackModel.register_params = user.register_params;
    // }

    Events.updateTraits(trackModel, true);
  } catch (e) {
    console.log(e);
  }
};

export const user = {
  namespaced: true,
  state: new State(),
  getters,
  mutations,
  actions,
};

export default {};
